import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Divider,
  CircularProgress,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Button,
  TableContainer,
  CssBaseline,
} from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Header3 from "../../Component/Header3";
import { useNavigate } from "react-router-dom";

const PaymentDetails = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortField, setSortField] = useState("All");
  const [sortOrder, setSortOrder] = useState("asc");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [customerHistory, setCustomerHistory] = useState(null);

  const navigate = useNavigate();
  useEffect(() => {
    const fetchPayments = async () => {
      try {
        const customerId = localStorage.getItem("customerId");
        const response = await axios.get(
          `https://sakhisshop.com/api/orders/customer/${customerId}/shop-summary`,
        );

        const mapped = (response.data || []).map((shop) => ({
          shopId: shop.shopId || null,
          shopName: shop.shopName || "N/A",
          totalAmount: shop.totalAmount ? Number(shop.totalAmount) : 0,
          paidAmount: shop.paidAmount ? Number(shop.paidAmount) : 0,
          pendingAmount: shop.pendingAmount ? Number(shop.pendingAmount) : 0,
          orders: shop.orders || [], // array of orders
          lastOrderId:
            shop.lastOrderId ||
            (shop.orders?.length > 0 ? shop.orders[0].orderId : "N/A"),
          lastOrderDate: shop.lastOrderDate
            ? new Date(shop.lastOrderDate)
            : shop.orders?.length > 0 && shop.orders[0].orderDate
              ? new Date(shop.orders[0].orderDate)
              : null,
        }));

        setPayments(mapped);
      } catch (err) {
        console.error("Error fetching payments:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPayments();
  }, []);

  const groupPaymentsByShop = (payments) => {
    const map = {};

    payments.forEach((p) => {
      if (!map[p.shopName]) {
        map[p.shopName] = {
          shopName: p.shopName,
          totalAmount: 0,
          paidAmount: 0,
          pendingAmount: 0,
          orders: [],
          lastOrderDate: p.orderDate,
          lastOrderId: p.orderId,
        };
      }

      map[p.shopName].totalAmount += p.totalAmount;
      map[p.shopName].paidAmount += p.paidAmount;
      map[p.shopName].pendingAmount += p.pendingAmount;
      map[p.shopName].orders.push(p);
      if (
        !map[p.shopName].lastOrderDate ||
        (p.orderDate && p.orderDate > map[p.shopName].lastOrderDate)
      ) {
        map[p.shopName].lastOrderDate = p.orderDate;
        map[p.shopName].lastOrderId = p.orderId; // keep that orderId
      }
    });

    return payments;
  };
  const handleOpenHistory = (row) => {
    const transactions = row.orders.map((o) => ({
      date: o.orderDate ? new Date(o.orderDate).toLocaleDateString() : "N/A",
      amount: o.totalAmount,
      pending: o.pendingAmount,
    }));

    setCustomerHistory({
      customerName: row.shopName,
      totalAmount: row.totalAmount,
      pendingAmount: row.pendingAmount,
      transactions,
    });

    setHistoryOpen(true);
  };

  // Filtered payments
  let filteredPayments = groupPaymentsByShop(
    payments
      .filter((payment) =>
        payment.shopName.toLowerCase().includes(searchQuery.toLowerCase()),
      )
      .filter((payment) => payment.pendingAmount > 0), // ✅ hide rows with pending = 0
  );

  // Optional: Sort after grouping
  filteredPayments.sort((a, b) => {
    if (sortField === "name") {
      return sortOrder === "asc"
        ? a.shopName.localeCompare(b.shopName)
        : b.shopName.localeCompare(a.shopName);
    } else if (sortField === "totalAmount") {
      return sortOrder === "asc"
        ? a.totalAmount - b.totalAmount
        : b.totalAmount - a.totalAmount;
    } else if (sortField === "paidAmount") {
      return sortOrder === "asc"
        ? a.paidAmount - b.paidAmount
        : b.paidAmount - a.paidAmount;
    } else if (sortField === "pendingAmount") {
      return sortOrder === "asc"
        ? a.pendingAmount - b.pendingAmount
        : b.pendingAmount - a.pendingAmount;
    }
    return 0;
  });

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        pb: 8,
        pt: "64px",
      }}
    >
      <Header3 />
      <CssBaseline />

      {/* Header */}
      <Container sx={{ mt: 4, mb: 5 }}>
        <Box bgcolor="#3d5a80" color="white" borderRadius={1} p={2} mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton onClick={() => navigate(-1)} color="warning">
              <ArrowBackIosNewIcon />
            </IconButton>
            <Typography variant="h4" sx={{ fontWeight: "bold" }}>
              Payment Details
            </Typography>
          </Box>
        </Box>
      </Container>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flex: 1,
          overflow: "auto",
          p: 4,
          px: { xs: 2, sm: 4, md: 8 },
          background: "linear-gradient(to right, #f0f4c3, #e3f2fd)",
        }}
      >
        <Container maxWidth="lg">
          {/* Section heading + search / sort controls */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
              Transactions
            </Typography>

            <Box
              display="flex"
              flexDirection={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "stretch", sm: "center" }}
              gap={2}
            >
              {/* Search Input */}
              <TextField
                variant="outlined"
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Enter shop name"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <VisibilityIcon />
                    </InputAdornment>
                  ),
                  style: { backgroundColor: "white" },
                }}
                sx={{ width: { xs: "100%", sm: 250 } }}
              />

              {/* Sort By Dropdown */}
              <FormControl variant="outlined" sx={{ width: 200 }}>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortField}
                  onChange={(e) => setSortField(e.target.value)}
                  label="Sort By"
                >
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value="name">Shop Name</MenuItem>
                  <MenuItem value="date">Order Date</MenuItem>
                  <MenuItem value="paidAmount">Paid Amount</MenuItem>
                  <MenuItem value="pendingAmount">Pending Amount</MenuItem>
                </Select>
              </FormControl>

              {/* Order Dropdown */}
              <FormControl variant="outlined" sx={{ width: 200 }}>
                <InputLabel>Order</InputLabel>
                <Select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  label="Order"
                >
                  <MenuItem value="asc">Ascending</MenuItem>
                  <MenuItem value="desc">Descending</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {loading ? (
            <Box sx={{ textAlign: "center", mt: 4 }}>
              <CircularProgress />
              <Typography>Loading payments...</Typography>
            </Box>
          ) : (
            <Box sx={{ width: "100%", overflowX: "auto" }}>
              <Table stickyHeader sx={{ minWidth: 900 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <b>Shop Name</b>
                    </TableCell>
                    <TableCell>
                      <b>Order ID</b>
                    </TableCell>

                    <TableCell>
                      <b>Order Date</b>
                    </TableCell>
                    <TableCell>
                      <b>Total (₹)</b>
                    </TableCell>
                    <TableCell>
                      <b>Paid (₹)</b>
                    </TableCell>
                    <TableCell>
                      <b>Pending (₹)</b>
                    </TableCell>
                    <TableCell>
                      <b>Actions</b>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredPayments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No payments found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredPayments.map((shop, index) => (
                      <TableRow hover key={shop.shopName}>
                        <TableCell>{shop.shopName}</TableCell>
                        <TableCell>
                          {shop.lastOrderId ||
                            (shop.orders?.length > 0
                              ? shop.orders[0].orderId
                              : "N/A")}
                        </TableCell>
                        <TableCell>
                          {shop.lastOrderDate
                            ? new Date(shop.lastOrderDate).toLocaleDateString()
                            : "N/A"}
                        </TableCell>
                        <TableCell>₹{shop.totalAmount.toFixed(2)}</TableCell>
                        <TableCell>₹{shop.paidAmount.toFixed(2)}</TableCell>
                        <TableCell>₹{shop.pendingAmount.toFixed(2)}</TableCell>
                        <TableCell>
                          <Tooltip title="View History">
                            <IconButton
                              color="primary"
                              onClick={() => handleOpenHistory(shop)}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Box>
          )}
        </Container>

        {/* History Dialog */}
        <Dialog
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>Transaction History</DialogTitle>
          <DialogContent dividers>
            {!customerHistory ? (
              <Typography>No history available.</Typography>
            ) : (
              <Box>
                <Typography variant="h6">
                  {customerHistory.customerName}
                </Typography>
                <Typography>
                  Total Amount: ₹{customerHistory.totalAmount}
                </Typography>
                <Typography>
                  Pending Amount: ₹{customerHistory.pendingAmount}
                </Typography>

                <TableContainer component={Paper} sx={{ mt: 2 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Total (₹)</TableCell>
                        <TableCell>Pending (₹)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {customerHistory.transactions.map((t, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{t.date}</TableCell>
                          <TableCell>₹{t.amount}</TableCell>
                          <TableCell>₹{t.pending}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setHistoryOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default PaymentDetails;
